import { MIN_DIMENSION, MAX_DIMENSION } from "pages/dtf-transfers/by-size/data";

const CLOUDINARY_CLOUD_NAME = "big-oven-tees-inc";
const CLOUDINARY_UPLOAD_PRESET = "tnedst8q";
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`;

const getDtfTransfersFolder = (emailid) => `Shirtly/${emailid}/DTFTransfers`;

const CUT_SEPARATELY_FEE = 0.5;
const ARTWORK_CHECK_FEE = 10;

const getDefaultArtworkState = () => ({
  width: 1.65,
  height: 1.65,
  quantity: 1,
  keepAspectRatio: false,
  removeBackground: false,
  cutSeparately: false,
  artworkCheck: false,
  upscale: false,
  sizeTab: "custom",
  popularSize: "",
  extraSizes: [],
});

export const getBaseUnitPrice = (width, height) =>
  Number((Number(width) * Number(height) * 0.01).toFixed(2));

export const getCuttingUnitPrice = (cutSeparately = false) =>
  cutSeparately ? CUT_SEPARATELY_FEE : 0;

export const getCuttingPrice = (quantity, cutSeparately = false) =>
  Number((getCuttingUnitPrice(cutSeparately) * Number(quantity)).toFixed(2));

export const getArtworkCheckFee = (artworkCheck = false) =>
  artworkCheck ? ARTWORK_CHECK_FEE : 0;

export const getUnitPrice = (width, height, cutSeparately = false) =>
  Number(
    (
      getBaseUnitPrice(width, height) + getCuttingUnitPrice(cutSeparately)
    ).toFixed(2),
  );

export const getSizeTotalPrice = (
  width,
  height,
  quantity,
  cutSeparately = false,
) =>
  Number(
    (getUnitPrice(width, height, cutSeparately) * Number(quantity)).toFixed(2),
  );

export const getTotalPrice = (
  width,
  height,
  quantity,
  cutSeparately = false,
  artworkCheck = false,
) =>
  Number(
    (
      getSizeTotalPrice(width, height, quantity, cutSeparately) +
      getArtworkCheckFee(artworkCheck)
    ).toFixed(2),
  );

export const clampDimension = (value) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return MIN_DIMENSION;
  return Math.min(MAX_DIMENSION, Math.max(MIN_DIMENSION, parsed));
};

/** Linked width/height while keeping aspectRatio = width / height. */
export const getAspectLinkedDimensions = (field, value, aspectRatio) => {
  const clamped = clampDimension(value);

  if (!aspectRatio || aspectRatio <= 0 || !Number.isFinite(aspectRatio)) {
    return { [field]: clamped };
  }

  const roundDimension = (dimension) =>
    clampDimension(Number(dimension.toFixed(2)));

  if (field === "width") {
    let width = clamped;
    let height = width / aspectRatio;

    if (height > MAX_DIMENSION) {
      height = MAX_DIMENSION;
      width = height * aspectRatio;
    } else if (height < MIN_DIMENSION) {
      height = MIN_DIMENSION;
      width = height * aspectRatio;
    }

    return {
      width: roundDimension(width),
      height: roundDimension(height),
    };
  }

  let height = clamped;
  let width = height * aspectRatio;

  if (width > MAX_DIMENSION) {
    width = MAX_DIMENSION;
    height = width / aspectRatio;
  } else if (width < MIN_DIMENSION) {
    width = MIN_DIMENSION;
    height = width / aspectRatio;
  }

  return {
    width: roundDimension(width),
    height: roundDimension(height),
  };
};

export const createArtworkStateFromCloudinary = (info) => ({
  ...getDefaultArtworkState(),
  id: info.public_id,
  file: null,
  fileName: info.original_filename || info.public_id,
  previewUrl: info.secure_url,
  secureUrl: info.secure_url,
  originalSecureUrl: info.secure_url,
  publicId: info.public_id,
  cloudinaryInfo: info,
});

export const createArtworkState = (file) => ({
  ...getDefaultArtworkState(),
  id: `${file.name}-${file.lastModified}`,
  file,
  fileName: file.name,
  previewUrl: URL.createObjectURL(file),
  secureUrl: null,
  publicId: null,
  cloudinaryInfo: null,
});

export const isImageFile = (file) => file?.type?.startsWith("image/");

export const revokeArtworkPreview = (artwork) => {
  if (artwork?.previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(artwork.previewUrl);
  }
};

export const uploadFileToCloudinary = async (file, emailid) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  formData.append("folder", getDtfTransfersFolder(emailid));

  const response = await fetch(CLOUDINARY_UPLOAD_URL, {
    method: "post",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Cloudinary upload failed");
  }

  return response.json();
};

export const openCloudinaryUploadWidget = (emailid, onSuccess) => {
  const myWidget = window.cloudinary.createUploadWidget(
    {
      cloudName: CLOUDINARY_CLOUD_NAME,
      show_powered_by: false,
      uploadPreset: CLOUDINARY_UPLOAD_PRESET,
      folder: getDtfTransfersFolder(emailid),
      clientAllowedFormats: ["png", "jpeg", "jpg", "pdf", "svg", "ai", "eps"],
      sources: ["local", "camera", "url"],
      multiple: false,
      maxFiles: 1,
    },
    (error, result) => {
      if (!error && result?.event === "success") {
        onSuccess(result.info);
        myWidget.close();
      }
    },
  );

  myWidget.open();
};

export const applyCloudinaryBackgroundRemoval = (secureUrl) => {
  if (!secureUrl?.includes("/upload/")) {
    return secureUrl;
  }

  const [prefix, suffix] = secureUrl.split("/upload/");
  return `${prefix}/upload/e_background_removal/${suffix}`;
};

export const preloadCloudinaryImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(url);
    image.onerror = () => {
      const retryUrl = `${url}?timestamp=${Date.now()}`;
      const retryImage = new Image();
      retryImage.onload = () => resolve(retryUrl);
      retryImage.onerror = reject;
      retryImage.src = retryUrl;
    };
    image.src = url;
  });

export const getArtworkOptionLabels = (item) => {
  const labels = [];
  if (item?.removeBackground) labels.push("Remove Background");
  if (item?.cutSeparately) labels.push("Cut Separately");
  if (item?.artworkCheck) labels.push("Artwork Check");
  if (item?.productType === "gang-sheet" && item?.mode === "create") {
    labels.push("Created Gang Sheet");
  }
  if (item?.productType === "gang-sheet" && item?.mode === "upload") {
    labels.push("Uploaded Gang Sheet");
  }
  return labels;
};

export const getDtfCartItemTitle = (item) =>
  item?.productType === "gang-sheet"
    ? "DTF Gang Sheet Builder"
    : "DTF Transfers By Size";

export const normalizeCartItem = (item) => {
  if (!item) return null;

  const cutSeparately = Boolean(item.cutSeparately);
  const artworkCheck = Boolean(item.artworkCheck);
  const removeBackground = Boolean(item.removeBackground);

  const sizes =
    Array.isArray(item.sizes) && item.sizes.length > 0
      ? item.sizes
      : item.width != null && item.height != null
        ? [
            {
              width: item.width,
              height: item.height,
              quantity: item.quantity ?? 1,
              unitPrice:
                item.unitPrice ??
                getUnitPrice(item.width, item.height, cutSeparately),
              totalPrice:
                item.totalPrice ??
                getSizeTotalPrice(
                  item.width,
                  item.height,
                  item.quantity ?? 1,
                  cutSeparately,
                ),
            },
          ]
        : [];

  const sizesQuantity = sizes.reduce(
    (sum, size) => sum + (size.quantity ?? 0),
    0,
  );
  const cuttingUnitPrice =
    item.cuttingUnitPrice ?? getCuttingUnitPrice(cutSeparately);
  const cuttingFee =
    item.cuttingFee ?? getCuttingPrice(sizesQuantity || 1, cutSeparately);
  const artworkCheckFee =
    item.artworkCheckFee ?? getArtworkCheckFee(artworkCheck);

  const sizesTotal = sizes.reduce(
    (sum, size) => sum + (size.totalPrice || 0),
    0,
  );

  const total = item.total ?? Number((sizesTotal + artworkCheckFee).toFixed(2));

  return {
    ...item,
    id:
      item.cartRecordId ??
      item.id ??
      item._id ??
      `${item.fileName || "item"}-${item.publicId || Date.now()}`,
    productType: item.productType || "by-size",
    mode: item.mode || null,
    sheetWidth: item.sheetWidth ?? null,
    sheetHeight: item.sheetHeight ?? null,
    fileName: item.fileName ?? item.file_name ?? "Artwork",
    previewUrl: item.previewUrl ?? item.secureUrl ?? item.preview_url,
    removeBackground,
    cutSeparately,
    cuttingUnitPrice,
    cuttingFee,
    artworkCheck,
    artworkCheckFee,
    sizes,
    total,
  };
};

export const normalizeCartEntry = (entry) => {
  if (!entry) return null;

  if (entry.item) {
    return normalizeCartItem({
      ...entry.item,
      cartRecordId: entry._id,
      cartId: entry.cartId,
    });
  }

  return normalizeCartItem(entry);
};

export const normalizeCartResponse = (response) => {
  const entries = Array.isArray(response)
    ? response
    : response?.item
      ? [response]
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.cart)
          ? response.cart
          : Array.isArray(response?.data)
            ? response.data
            : [];

  const items = entries.map(normalizeCartEntry).filter(Boolean);

  return {
    items,
    count: response?.count ?? items.length,
  };
};

export const prepareCartItemForApi = (item) => {
  const { _id, cartRecordId, cartId, id, ...itemData } = item || {};

  return itemData;
};

export const buildCartItemWithQuantity = (item, quantity) => {
  const cutSeparately = Boolean(item.cutSeparately);
  const artworkCheck = Boolean(item.artworkCheck);

  const sizes = (item.sizes || []).map((size, index) => {
    if (index !== 0) return size;

    const unitPrice =
      size.unitPrice ?? getUnitPrice(size.width, size.height, cutSeparately);

    return {
      width: size.width,
      height: size.height,
      quantity,
      unitPrice,
      totalPrice: Number((unitPrice * Number(quantity)).toFixed(2)),
    };
  });

  const sizesQuantity = sizes.reduce(
    (sum, size) => sum + (size.quantity ?? 0),
    0,
  );
  const cuttingFee = getCuttingPrice(sizesQuantity, cutSeparately);
  const artworkCheckFee = getArtworkCheckFee(artworkCheck);

  return prepareCartItemForApi({
    ...item,
    removeBackground: Boolean(item.removeBackground),
    cutSeparately,
    cuttingUnitPrice: getCuttingUnitPrice(cutSeparately),
    cuttingFee,
    artworkCheck,
    artworkCheckFee,
    sizes,
    total: Number(
      (
        sizes.reduce((sum, size) => sum + (size.totalPrice || 0), 0) +
        artworkCheckFee
      ).toFixed(2),
    ),
  });
};

export const buildCartItem = (artwork) => {
  const cutSeparately = Boolean(artwork.cutSeparately);
  const artworkCheck = Boolean(artwork.artworkCheck);

  const mainSize = {
    width: artwork.width,
    height: artwork.height,
    quantity: artwork.quantity,
    unitPrice: getUnitPrice(artwork.width, artwork.height, cutSeparately),
    totalPrice: getSizeTotalPrice(
      artwork.width,
      artwork.height,
      artwork.quantity,
      cutSeparately,
    ),
  };

  const sizes = [
    mainSize,
    ...artwork.extraSizes.map((size) => ({
      width: size.width,
      height: size.height,
      quantity: size.quantity,
      unitPrice: getUnitPrice(size.width, size.height, cutSeparately),
      totalPrice: getSizeTotalPrice(
        size.width,
        size.height,
        size.quantity,
        cutSeparately,
      ),
    })),
  ];

  const sizesQuantity = sizes.reduce(
    (sum, size) => sum + (size.quantity ?? 0),
    0,
  );
  const cuttingFee = getCuttingPrice(sizesQuantity, cutSeparately);
  const artworkCheckFee = getArtworkCheckFee(artworkCheck);

  return {
    id: `${artwork.id}-${Date.now()}`,
    productType: "by-size",
    fileName: artwork.fileName,
    previewUrl: artwork.previewUrl,
    secureUrl: artwork.secureUrl,
    publicId: artwork.publicId,
    removeBackground: Boolean(artwork.removeBackground),
    cutSeparately,
    cuttingUnitPrice: getCuttingUnitPrice(cutSeparately),
    cuttingFee,
    artworkCheck,
    artworkCheckFee,
    sizes,
    total: Number(
      (
        sizes.reduce((sum, size) => sum + size.totalPrice, 0) + artworkCheckFee
      ).toFixed(2),
    ),
  };
};

export const canAddToCart = (artwork) =>
  Boolean(
    artwork?.secureUrl &&
    artwork?.width >= MIN_DIMENSION &&
    artwork?.height >= MIN_DIMENSION &&
    artwork?.quantity >= 1 &&
    getBaseUnitPrice(artwork?.width, artwork?.height) > 0,
  );
